import { cn, formatBatteryPercent, formatPhoneNumber, formatRelativeTime, getDeviceStatus } from '@/lib/utils';
import { useAuthStore } from '@/stores/useAuthStore';
import { PermissionBadge } from '@/components/devices/PermissionBadge';
import { DeviceDetailsForm } from '@/components/devices/DeviceDetailsForm';
import { TrackingSettingsForm } from '@/components/devices/TrackingSettingsForm';
import { ArchiveDeviceDialog } from '@/components/devices/ArchiveDeviceDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Smartphone, Tablet, ShieldAlert, ShieldOff, Settings, Battery, Archive } from 'lucide-react';
import type { Device } from '@/types';

const statusDotColors: Record<string, string> = {
  online: 'bg-green-500',
  stale: 'bg-gray-300',
  offline: 'bg-gray-400',
};

interface DeviceListItemProps {
  device: Device;
  isSelected: boolean;
  onClick: () => void;
}

export function DeviceListItem({ device, isSelected, onClick }: DeviceListItemProps) {
  const status = getDeviceStatus(device.last_checkin_at);
  const primaryLabel = formatPhoneNumber(device.phone_number) ?? device.device_name;
  const hasPermissionWarning = device.permission_status === 'denied' || device.permission_status === 'foreground_only';
  const isAdmin = useAuthStore((s) => s.manager?.role === 'admin');
  const isArchived = device.archived_at != null;

  return (
    <div className="relative">
      <button
        onClick={onClick}
        className={cn(
          'flex w-full items-center gap-3 border-b border-gray-100 px-4 py-3 text-left transition-colors',
          isSelected ? 'bg-brand-purple-subtle border-b-0' : 'hover:bg-gray-50'
        )}
      >
        <span
          className={cn(
            'h-2.5 w-2.5 flex-shrink-0 rounded-full',
            isArchived ? 'bg-gray-300' : statusDotColors[status]
          )}
        />
        <div className="min-w-0 flex-1">
          <div
            className={cn(
              'flex items-center gap-1.5 truncate text-sm font-medium',
              isArchived ? 'text-gray-400' : 'text-brand-black'
            )}
          >
            {primaryLabel}
            {device.device_type && (
              device.device_type === 'ios'
                ? <Smartphone className="h-3 w-3 flex-shrink-0 text-gray-400" />
                : <Tablet className="h-3 w-3 flex-shrink-0 text-gray-400" />
            )}
            {hasPermissionWarning && !isArchived && (
              device.permission_status === 'denied'
                ? <ShieldOff className="h-3 w-3 flex-shrink-0 text-brand-orange" />
                : <ShieldAlert className="h-3 w-3 flex-shrink-0 text-amber-500" />
            )}
            {isArchived && (
              <span className="flex flex-shrink-0 items-center gap-0.5 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500">
                <Archive className="h-2.5 w-2.5" />
                Archived
              </span>
            )}
          </div>
        </div>
        {!isSelected && (
          <span className="flex-shrink-0 text-xs text-gray-400">
            {formatRelativeTime(device.last_checkin_at)}
          </span>
        )}
      </button>

      {isSelected && (
        <div className="space-y-2 border-b border-gray-200 bg-brand-purple-subtle px-4 pb-3 pt-1">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
            {device.battery_level != null && (
              <span className="flex items-center gap-1">
                <Battery className="h-3 w-3" /> {formatBatteryPercent(device.battery_level)}
              </span>
            )}
            {device.os_version && <span>OS: {device.os_version}</span>}
            {device.app_version && <span>App Version: {device.app_version}</span>}
            {device.serial_number && <span>Serial: {device.serial_number}</span>}
            <span className="ml-auto text-gray-400">{formatRelativeTime(device.last_checkin_at)}</span>
          </div>

          {device.permission_status !== 'unknown' && (
            <PermissionBadge status={device.permission_status} isPrecise={device.is_precise_location} />
          )}
        </div>
      )}

      {isSelected && isAdmin && (
        <div className="absolute right-2 top-2 flex items-center gap-0.5">
          <ArchiveDeviceDialog device={device} />
          {!isArchived && (
            <Dialog>
              <DialogTrigger>
                <button className="rounded p-1 text-gray-400 hover:bg-white hover:text-brand-purple">
                  <Settings className="h-3.5 w-3.5" />
                </button>
              </DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Device Settings</DialogTitle>
                </DialogHeader>
                <div className="space-y-5">
                  <section>
                    <h3 className="mb-2 text-sm font-semibold text-brand-black">Device details</h3>
                    <DeviceDetailsForm device={device} />
                  </section>
                  <Separator />
                  <section>
                    <h3 className="mb-2 text-sm font-semibold text-brand-black">Tracking settings</h3>
                    <TrackingSettingsForm device={device} />
                  </section>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      )}
    </div>
  );
}
